import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { trackEvent } from "@/lib/analytics";

export type ViewMode = "standard" | "livestream";

interface ViewModeContextValue {
  viewMode: ViewMode;
  isLivestream: boolean;
  setViewMode: (mode: ViewMode, source?: string) => void;
  exitLivestream: (source?: string) => void;
}

const STORAGE_KEY = "babblegram_view_mode";
const ViewModeContext = createContext<ViewModeContextValue | undefined>(undefined);

const readStoredViewMode = (): ViewMode => {
  if (typeof window === "undefined") return "standard";
  return window.localStorage.getItem(STORAGE_KEY) === "livestream" ? "livestream" : "standard";
};

export const ViewModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [viewModeState, setViewModeState] = useState<ViewMode>(readStoredViewMode);

  const setViewMode = useCallback((mode: ViewMode, source = "unknown") => {
    setViewModeState(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // The view still changes when storage is unavailable.
    }
    trackEvent(mode === "livestream" ? "livestream_enter" : "livestream_exit", { source });
  }, []);

  const exitLivestream = useCallback((source = "exit_control") => {
    setViewMode("standard", source);
  }, [setViewMode]);

  useEffect(() => {
    const syncViewMode = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setViewModeState(event.newValue === "livestream" ? "livestream" : "standard");
      }
    };
    window.addEventListener("storage", syncViewMode);
    return () => window.removeEventListener("storage", syncViewMode);
  }, []);

  const value = useMemo(() => ({
    viewMode: viewModeState,
    isLivestream: viewModeState === "livestream",
    setViewMode,
    exitLivestream,
  }), [viewModeState, setViewMode, exitLivestream]);

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (!context) throw new Error("useViewMode must be used within ViewModeProvider");
  return context;
};
